import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { store } from '@/store';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { api } from '@/store/api';
import { useAmazonConnectContacts } from '@/hooks/useAmazonConnectContacts';
import {
  getInitialOrCaptureContactId,
  hasActiveCallLeg,
} from '@/components/amazon-connect/contactUtils';
import {
  Paper,
  Typography,
  Box,
  TextField,
  CircularProgress,
  Snackbar,
} from '@gapartners/ui';
import { setSessionCaseInfo } from '@/store/slices/callSlice';
import { setCaseInfo } from '@/store/slices/caseSlice';
import {
  getCaseNotesAppendDelta,
  getCaseNotesSaveEligibility,
  isCaseNotesAppendOnlyChange,
} from '@/utils/caseNotes';
import { CaseNotesSaveStatus } from '@/components/contact-center/CaseNotesSaveStatus';

const { useUpdateCaseMutation } = api;

const AUTO_SAVE_DELAY_MS = 5000;

interface NotesSaveTarget {
  contactId: string;
  caseId: number;
}

export function NotesCard() {
  const { t } = useTranslation();
  const { contactId } = useAppSelector(state => state.call);
  const { activeContacts } = useAmazonConnectContacts();
  const activeContact = activeContacts.find(c => {
    if (!contactId || !hasActiveCallLeg(c)) return false;
    try {
      return (
        c.getContactId() === contactId ||
        getInitialOrCaptureContactId(c) === contactId
      );
    } catch {
      return false;
    }
  });
  const isOutboundCall = Boolean(activeContact && !activeContact.isInbound());
  const dispatch = useAppDispatch();
  const currentCase = useAppSelector(state => state.case.currentCase);

  const [updateCaseNotes, { isLoading: isUpdating }] = useUpdateCaseMutation();

  const [localNotes, setLocalNotes] = useState('');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [editRejected, setEditRejected] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  // Ref to track if we have initialized from DB to avoid overwriting with empty string
  const initializedRef = useRef(false);
  // Last payload successfully saved; used for dirty check (server may append metadata on read)
  const lastSavedNotesRef = useRef<string | null>(null);
  // Payload of the save in flight; counts as saved so the same text is never appended twice
  const savingNotesRef = useRef<string | null>(null);
  // Contact the card currently shows; a save resolving after a switch must not touch its state
  const activeContactIdRef = useRef(contactId);
  // Last committed case + notes, read on contact switch / unmount to flush pending notes
  const committedRef = useRef<{ caseId?: number; notes: string }>({
    notes: '',
  });

  useEffect(() => {
    committedRef.current = { caseId: currentCase?.id, notes: localNotes };
  });

  const saveNotes = useCallback(
    (target: NotesSaveTarget, notes: string) => {
      const notesDelta = getCaseNotesAppendDelta(
        savingNotesRef.current ?? lastSavedNotesRef.current,
        notes
      );
      if (notesDelta === null) return;

      const isActiveContact = () =>
        activeContactIdRef.current === target.contactId;
      savingNotesRef.current = notes;

      updateCaseNotes({ caseId: target.caseId, notes: notesDelta })
        .unwrap()
        .then(() => {
          const { call, case: caseState } = store.getState();
          const sessionCase = call.sessions[target.contactId]?.currentCase;
          if (sessionCase?.id === target.caseId) {
            dispatch(
              setSessionCaseInfo({
                contactId: target.contactId,
                caseInfo: { ...sessionCase, notes },
              })
            );
          }
          if (!isActiveContact()) return;

          lastSavedNotesRef.current = notes;
          setLastSaved(new Date());
          if (caseState.currentCase?.id === target.caseId) {
            dispatch(setCaseInfo({ ...caseState.currentCase, notes }));
          }
        })
        .catch((err: unknown) => {
          console.error('Failed to save notes:', err);
          setSaveFailed(true);
        })
        .finally(() => {
          if (isActiveContact() && savingNotesRef.current === notes) {
            savingNotesRef.current = null;
          }
        });
    },
    [updateCaseNotes, dispatch]
  );

  // Reset initialized state when contactId changes. On contact switch or
  // unmount, save notes still waiting for the debounce instead of dropping them.
  useEffect(() => {
    activeContactIdRef.current = contactId;
    initializedRef.current = false;
    lastSavedNotesRef.current = null;
    savingNotesRef.current = null;
    setLocalNotes('');
    setLastSaved(null);
    setEditRejected(false);

    return () => {
      const { caseId, notes } = committedRef.current;
      if (contactId && caseId) saveNotes({ contactId, caseId }, notes);
    };
  }, [contactId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Declared after the reset above so a card mounted mid-call loads the
  // case notes instead of having the reset wipe them.
  useEffect(() => {
    if (currentCase?.notes !== undefined && !initializedRef.current) {
      setLocalNotes(currentCase.notes);
      lastSavedNotesRef.current = currentCase.notes;
      initializedRef.current = true;
    }
  }, [currentCase]);

  // Debounced save
  useEffect(() => {
    // Require active call, linked case, and initialized state before persisting notes.
    if (!contactId || !currentCase?.id || !initializedRef.current) return;
    const savedBaseline = savingNotesRef.current ?? lastSavedNotesRef.current;
    if (getCaseNotesAppendDelta(savedBaseline, localNotes) === null) return;

    const target = { contactId, caseId: currentCase.id };
    const timer = setTimeout(
      () => saveNotes(target, localNotes),
      AUTO_SAVE_DELAY_MS
    );

    return () => clearTimeout(timer);
  }, [localNotes, contactId, currentCase, saveNotes]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Saved notes are append-only. An edit to them can never be saved and
    // would silently stall auto-save, so reject it and tell the agent why.
    if (
      !isCaseNotesAppendOnlyChange(
        savingNotesRef.current ?? lastSavedNotesRef.current,
        e.target.value
      )
    ) {
      setEditRejected(true);
      return;
    }
    setEditRejected(false);
    setLocalNotes(e.target.value);
    initializedRef.current = true;
  };

  const handleDiscardNotes = () => {
    setLocalNotes(lastSavedNotesRef.current ?? '');
    setEditRejected(false);
  };

  const isSaving = isUpdating;

  const notesSaveEligibility = getCaseNotesSaveEligibility(
    lastSavedNotesRef.current,
    localNotes
  );
  const hasUnsavedNotes = notesSaveEligibility === 'appendable';

  // A pending save cannot finish once the page unloads, so ask the browser to confirm.
  useEffect(() => {
    if (!hasUnsavedNotes) return;
    const warnBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [hasUnsavedNotes]);

  /** Case notes require a linked case. Direct-connect calls may now have a dummy case. */
  const notesDisabled = !contactId || isOutboundCall || !currentCase?.id;
  const noActiveCall = !contactId;

  const getNotesDisabledReason = () => {
    if (noActiveCall) {
      return t('components.contactCenter.notesActiveCallOnly');
    }
    if (currentCase?.id === undefined) {
      return t('components.contactCenter.notesCaseNotLinked');
    }
    return t('components.contactCenter.quickNotesNotAvailableEditOutbound');
  };
  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          height: '22rem',
          maxHeight: '22rem',
          flexShrink: 0,
          overflow: 'hidden',
          ...(notesDisabled && {
            opacity: 0.85,
          }),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            {t('components.contactCenter.quickNotes')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isSaving && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircularProgress size={12} />
                <Typography variant="caption" color="text.secondary">
                  {t('components.contactCenter.saving')}
                </Typography>
              </Box>
            )}
            {!isSaving && (
              <>
                <CaseNotesSaveStatus
                  lastSaved={lastSavedNotesRef.current}
                  current={localNotes}
                  autoSave
                  onDiscard={handleDiscardNotes}
                />
                {notesSaveEligibility === 'unchanged' && lastSaved && (
                  <Typography variant="caption" color="text.secondary">
                    {t('components.contactCenter.saved')}{' '}
                    {lastSaved.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>

        {notesDisabled && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1.5, flexShrink: 0 }}
          >
            {getNotesDisabledReason()}
          </Typography>
        )}

        {editRejected && (
          <Typography
            variant="body2"
            color="warning.main"
            sx={{ mb: 1.5, flexShrink: 0 }}
          >
            {t('components.contactCenter.caseNotesAppendOnly')}
          </Typography>
        )}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <TextField
            multiline
            fullWidth
            placeholder={t('components.contactCenter.notesPlaceholder')}
            value={localNotes}
            onChange={handleChange}
            variant="outlined"
            disabled={notesDisabled}
            sx={{
              height: '100%',
              '& .MuiOutlinedInput-root': {
                height: '100%',
                alignItems: 'flex-start',
                overflow: 'hidden',
              },
              '& textarea': {
                height: '100% !important',
                maxHeight: '100% !important',
                overflow: 'auto !important',
                resize: 'none',
                boxSizing: 'border-box',
              },
            }}
          />
        </Box>
      </Paper>

      <Snackbar
        open={saveFailed}
        onClose={() => setSaveFailed(false)}
        message={t('components.contactCenter.caseNotesUpdateFailed')}
        variant="toast"
        severity="error"
        autoHideDuration={5000}
      />
    </>
  );
}
